import { set, toJS } from 'mobx';
import { types, Instance, flow, getEnv } from 'mobx-state-tree';
import { RootServices } from '../../../../types/RootStore';
import {
  AddressAutocompleteDetailsContract,
  AddressAutocompleteMatch,
  AddressAutocompleteResponseMatch,
} from '../../../../types/AddressValidation';

const AddressMatch = types.model({
  description: types.string,
  placeId: types.string,
});

export const AddressAutocompleteModel = types
  .model({
    customerInput: types.maybeNull(types.string),
    addressMatches: types.optional(types.array(AddressMatch), []),
    cachedInput: types.maybeNull(types.string),
  })
  .volatile(() => ({
    isWorking: false,
    isEnabled: false,
  }))
  .views(self => ({
    get hasMatches(): boolean {
      return self.isEnabled && self.addressMatches.length > 0;
    },
    get matchList(): AddressAutocompleteMatch[] {
      return toJS(self.addressMatches);
    },
  }))
  .actions(self => {
    const { addressAutocompleteService, isoCodeService } =
      getEnv<RootServices>(self);
    const setIsEnabled = (isEnabled = true): void => {
      set(self, { isEnabled });
    };
    const setIsWorking = (isWorking = true): void => {
      set(self, { isWorking });
    };
    const setCustomerInput = (address1: string): void => {
      set(self, { customerInput: address1 });
    };
    const setCachedInput = (address1: string): void => {
      set(self, { cachedInput: address1 });
    };
    const clearCachedInput = (): void => {
      set(self, { cachedInput: null });
    };
    const setMatches = (matches: AddressAutocompleteResponseMatch[]): void => {
      const mutatedMatches = matches.map(match => {
        const { description, placeId } = match;
        return {
          description,
          placeId,
        };
      });
      set(self, { addressMatches: mutatedMatches });
    };
    const clearMatches = (): void => {
      setMatches([]);
    };

    // TODO: Consider renaming
    const getMatchesFlow = flow(function* (address1: string) {
      if (!self.isEnabled) return;
      if (address1 === self.cachedInput) {
        return;
      }
      setIsWorking();
      setCustomerInput(address1);
      const result = yield addressAutocompleteService.getMatches(address1);
      if (!result) {
        clearCachedInput();
        clearMatches();
        setIsWorking(false);
        return;
      }
      setMatches(result);
      setCachedInput(address1);
      setIsWorking(false);
    });

    const getMatchDetails = async (
      placeId: string
    ): Promise<AddressAutocompleteDetailsContract | null> => {
      if (!self.isEnabled) return null;
      const result = await addressAutocompleteService.getDetails(placeId);
      if (!result) {
        return null;
      }
      const countryCode = result.country === 'USA' ? 'US' : result.country;
      const countryName = await isoCodeService.getNameFromIsoCode(countryCode);

      return {
        ...result,
        country: countryName || result.country,
      };
    };

    return {
      setIsEnabled,
      getMatchesFlow,
      getMatchDetails,
      setMatches, // exported for testing purposes
      clearMatches, // we will call this when the user selects a match OR dismisses the list
    };
  });

export interface IAddressAutocomplete
  extends Instance<typeof AddressAutocompleteModel> {}
